// src/api/graphClient.ts — SCE Issues
// Wrapper fetch authentifié pour Microsoft Graph API

import { tokenRequest } from '@/auth/msalConfig'
import { compteConnu, obtenirJeton } from '@/auth/session'
import { suivreRequete } from './activiteReseau'

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0'

// ─── Erreur domaine ───

export class GraphApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'GraphApiError'
  }
}

// ─── Récupération d'un token silencieux ───

async function getAccessToken(): Promise<string> {
  const account = compteConnu()
  if (!account) {
    throw new GraphApiError('Utilisateur non authentifié', 401)
  }
  return obtenirJeton(tokenRequest, account)
}

// ─── Options de requête ───

export interface GraphFetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT'
  body?: unknown
  headers?: Record<string, string>
  // Pour les uploads binaires
  rawBody?: BodyInit
  // Force l'URL telle quelle. Inutile en pratique : une URL commençant par
  // http(s):// est déjà détectée comme absolue (cf. estUrlAbsolue).
  absoluteUrl?: boolean
}

/**
 * Une URL déjà complète ne doit JAMAIS être préfixée par GRAPH_BASE.
 *
 * Le cas réel : les `@odata.nextLink` de pagination sont des URL absolues.
 * Les relayer à `graphGet` produisait
 * `https://graph.microsoft.com/v1.0https://graph.microsoft.com/v1.0/sites/…`,
 * chemin que Graph ne sait pas router — et auquel il répond, de façon très
 * trompeuse, `InvalidAuthenticationToken / Access token is empty`. Le bug ne
 * se voyait qu'une fois une liste passée au-dessus de 200 items (première
 * page suivante). On centralise donc la détection ici plutôt que de compter
 * sur chaque appelant pour penser à `absoluteUrl: true`.
 */
function estUrlAbsolue(path: string): boolean {
  return /^https?:\/\//i.test(path)
}

/**
 * Wrapper principal pour appeler Graph API.
 * Injecte automatiquement le Bearer token et gère les erreurs HTTP.
 *
 * @param path Chemin relatif (ex: "/me") ou URL absolue
 * @param options Méthode, body, headers
 * @returns Réponse JSON typée
 */
export function graphFetch<T = unknown>(
  path: string,
  options: GraphFetchOptions = {},
): Promise<T> {
  // Comptée pour l'écran d'ouverture de session (cf. activiteReseau.ts).
  return suivreRequete(() => executerGraph<T>(path, options))
}

async function executerGraph<T>(path: string, options: GraphFetchOptions): Promise<T> {
  const token = await getAccessToken()

  const url = options.absoluteUrl || estUrlAbsolue(path) ? path : `${GRAPH_BASE}${path}`

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  }

  let body: BodyInit | undefined
  if (options.rawBody !== undefined) {
    body = options.rawBody
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body)
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }
  }

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    ...(body !== undefined ? { body } : {}),
  })

  // ─── Gestion des erreurs HTTP ───
  if (!response.ok) {
    let details: unknown
    try {
      details = await response.json()
    } catch {
      details = await response.text().catch(() => null)
    }

    switch (response.status) {
      case 401:
        throw new GraphApiError('Session expirée — veuillez vous reconnecter', 401, details)
      case 403:
        throw new GraphApiError('Accès refusé — permissions insuffisantes', 403, details)
      case 404:
        throw new GraphApiError('Ressource introuvable', 404, details)
      case 429:
        throw new GraphApiError('Trop de requêtes — réessayer dans un instant', 429, details)
      case 500:
      case 502:
      case 503:
      case 504:
        throw new GraphApiError('Erreur serveur Graph API', response.status, details)
      default:
        throw new GraphApiError(
          `Erreur Graph API (${response.status})`,
          response.status,
          details,
        )
    }
  }

  // Certaines réponses (DELETE, PUT binary upload partiel) n'ont pas de body
  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json')) {
    return (await response.json()) as T
  }

  return (await response.text()) as unknown as T
}

// ─── Helpers pratiques ───

export function graphGet<T>(path: string): Promise<T> {
  return graphFetch<T>(path, { method: 'GET' })
}

export function graphPost<T>(path: string, body: unknown): Promise<T> {
  return graphFetch<T>(path, { method: 'POST', body })
}

export function graphPatch<T>(path: string, body: unknown): Promise<T> {
  return graphFetch<T>(path, { method: 'PATCH', body })
}

export function graphDelete<T = void>(path: string): Promise<T> {
  return graphFetch<T>(path, { method: 'DELETE' })
}
